import styles from './buildCard.module.sass';
import React, { FC, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { getDeviceOS } from '../../utils';
import Col from 'react-bootstrap/cjs/Col';
import { BuildArtefacts } from '../buildArtefact/buildArtefacts';
import { Badge, DropdownButton, DropdownDivider } from 'react-bootstrap';
import Dropdown from 'react-bootstrap/Dropdown';
import { useParams } from 'react-router-dom';
import { useToolAuthorisationClaimsContext } from '../../authentication/ToolAuthorisationClaimsContext';
import { IBuild } from '../../models/Build/Builld';
import { BuildCardContent } from './buildCardContent';


interface PropsType {
    build: IBuild;
    expanded: boolean;
    handleMakeBuildPublic: (build: IBuild) => void;
    handleShareBuild: (build: IBuild) => void;
    handleAddReleaseNotes: (build: IBuild) => void;
    handleClearReleaseNotes: (build: IBuild) => void;
}


const BuildCard: FC<PropsType> = observer((props) => {

    const os = getDeviceOS();
    const [copyVersionText, setCopyVersionText] = useState<string>('Copy version');
    const [copyPermalinkText, setPermalinkText] = useState<string>('Copy permalink');

    const { projectId } = useParams<{ projectId: string }>();
    const toolAuthorisationClaimsContext = useToolAuthorisationClaimsContext();

    const shouldRenderBuild = (build: IBuild): boolean => {
        if (os === 'ios')
            return build.iosArtefacts().length > 0;
        else if (os === 'android')
            return build.androidArtefacts().length > 0;
        else
            return true;
    };

    const getBuildTypeCssClass = (type: string) => {

        switch (type.toLowerCase()) {
            case 'nightly':
                return styles.nightBuild;
            case 'intraday':
                return styles.dayBuild;
            case 'production':
                return styles.productionBuild;
            case 'adhoc':
                return styles.qasBuild;
            default:
                return styles.genericBuild;
        }
    };

    const handleCopyVersion = (build: IBuild) => {

        navigator.clipboard.writeText(build.artefacts[0].version).then(() => {

                setCopyVersionText('Copied!');

                setTimeout(() => {
                    setCopyVersionText('Copy version');
                }, 2000);
            }
        );
    };

    const handleCopyPermalink = (build: IBuild) => {

        navigator.clipboard.writeText(build.getPermalink(projectId)).then(() => {

                setPermalinkText('Copied!');

                setTimeout(() => {
                    setPermalinkText('Copy permalink');
                }, 2000);
            }
        );
    };

    const formatBuildId = (buildId: string) => {
        return buildId.replaceAll('_', ' ');
    };

    return (
        shouldRenderBuild(props.build) && (
            <Col xs={12} sm={12} lg={12} xl={12} key={props.build.id}>
                <div className={`${styles.buildCard} ${getBuildTypeCssClass(props.build.type)}`}>
                    <div className={styles.buildId}>
                        {formatBuildId(props.build.id)}
                        <div className={styles.triggeredBy}>{props.build.readableTriggeredBy()}</div>
                    </div>

                    <div className={styles.buildDateWrapper}>
                        <div className={styles.buildDate}>
                            <i className="fas fa-calendar-day" /> {props.build.readableCreateAt()}
                            <div className={styles.triggeredByUsername}>{props.build.triggeredByUsername}</div>
                        </div>
                        {props.build.isSharedWithUser && (
                            <Badge pill bg="secondary" className={styles.public}>
                                <i className="fas fa-share-alt me-2" />
                                Shared with you
                            </Badge>
                        )}
                        {!props.build.isSharedWithUser && props.build.isPublic && (
                            <Badge pill bg="secondary" className={styles.public}>
                                <i className="fas fa-users me-2" />
                                Public
                            </Badge>
                        )}
                    </div>

                    {!!props.build.releaseNotes && props.build.releaseNotes !== '' &&
                        <BuildCardContent expanded={props.expanded} text={props.build.releaseNotes}
                                          renderBasedOnHeight={true} />}

                    <BuildArtefacts build={props.build} os={os} />

                    <DropdownButton drop="start" autoClose="outside" variant="outline-secondary"
                                    className={styles.buildLinkButton} title={<i className={'fas fa-ellipsis-v'} />}>
                        {!toolAuthorisationClaimsContext.MiniclipPublicAccess && (
                            <Dropdown.Item as="button" onClick={() => handleCopyVersion(props.build)}>
                                <i className="far fa-copy" />
                                {copyVersionText}
                            </Dropdown.Item>
                        )}
                        <Dropdown.Item as="button" onClick={() => handleCopyPermalink(props.build)}>
                            <i className="far fa-copy" />
                            {copyPermalinkText}
                        </Dropdown.Item>

                        {!toolAuthorisationClaimsContext.MiniclipPublicAccess && (
                            <Dropdown.Item as="button">
                                <a href={props.build.getFirestoreLink(projectId)} target="_blank" rel="noreferrer">
                                    <i className="fas fa-external-link-alt" />
                                    Open in Firestore
                                </a>
                            </Dropdown.Item>
                        )}

                        {toolAuthorisationClaimsContext.BuildsAdmin && (
                            <>
                                <DropdownDivider />
                                <Dropdown.Item as="button" onClick={() => props.handleMakeBuildPublic(props.build)}>
                                    {props.build.isPublic ? (
                                        <>
                                            <i className="fas fa-user-lock" />
                                            Make private
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-users" />
                                            Make public
                                        </>
                                    )}
                                </Dropdown.Item>

                                <Dropdown.Item as="button" onClick={() => props.handleShareBuild(props.build)}>
                                    <i className="fas fa-share-alt" />
                                    Share build
                                </Dropdown.Item>

                                <Dropdown.Item as="button" onClick={() => props.handleAddReleaseNotes(props.build)}>
                                    <i className="fas fa-pen" />
                                    {props.build.releaseNotes ? 'Edit release notes' : 'Add release notes'}
                                </Dropdown.Item>

                                {props.build.releaseNotes && (
                                    <Dropdown.Item as="button"
                                                   onClick={() => props.handleClearReleaseNotes(props.build)}>
                                        <i className="fas fa-eraser" />
                                        Clear Release Notes
                                    </Dropdown.Item>
                                )}
                            </>
                        )}
                    </DropdownButton>
                </div>
            </Col>
        )
    );
});

export default BuildCard;
